import { Component, OnInit, ViewChild } from '@angular/core';
import { DashboardService } from '../dashboard.service';
import { Search } from '../../shared/interfaces/search.interface';
import { Sale } from '../sale.interface';
import Enums from '../../utils/enums';
import { SelectItem } from 'primeng/api';
import { Calendar } from 'primeng/calendar';
import { Table } from 'primeng/table';
import { orderBy } from 'lodash';

@Component({
    selector: 'sales-component',
    templateUrl: './sales.component.html'
})
export class SalesComponent implements OnInit {
    sales: Sale[];
    total: Total[];
    filteredSales: Sale[];
    isLoading: boolean;
    rowGroupMetadata: any;
    @ViewChild('salesTable') salesTable: Table;

    zoneList: SelectItem[];
    paymentMethodList: SelectItem[];
    productList: SelectItem[];

    cols: any[];
    search: Search;
    recordsCounter: number;
    parkingName: string;

    @ViewChild('rangeCalendar') rangeCalendar: Calendar;
    rangeDates: Date[];
    isFirstSelected = false;

    constructor(
        private dashboardService: DashboardService
    ) { }

    ngOnInit(): void {
        const now = new Date();
        const nowString = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`
        this.rangeDates = [now, now];

        this.search = {
            StartDate: nowString,
            EndDate: nowString
        }

        this.cols = [
            { field: 'zone', header: 'Zona' },
            { field: 'paymentMethodType', header: 'Metoda de plata' },
            { field: 'code', header: 'Produs' },
            { field: 'qty', header: 'Nr. tranzactii' },
            { field: 'amount', header: 'Valoare' },
            { field: 'amountNET', header: 'Valoare neta' },
            { field: 'commission', header: 'Comision procesatori' },
            { field: 'currency', header: 'Valuta' }
        ];

        this.getSales(this.search);
    }

    selectDates(date: Date): void {
        const stringDate: string = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

        if (!this.isFirstSelected) {
            this.search.StartDate = stringDate;
            this.isFirstSelected = true;
        } else {
            this.search.EndDate = stringDate;
            this.isFirstSelected = false;
            this.getSales(this.search);
            this.rangeCalendar.overlayVisible = false;
        }
    }

    getSales(search: Search): void {
        this.isLoading = true;
        this.sales = [];
        this.total = [];
        this.recordsCounter = 0;

        this.dashboardService.getSales(search).subscribe(data => {
            for (const sale of data) {
                if (!sale.amount)
                    sale.amount = 0;

                sale.amountNET = this.getNETAmount(sale.amount);
                sale.commission = sale.amount - sale.amountNET;
            }

            this.sales = data.sort((a, b) => (a.paymentMethodTypeID < b.paymentMethodTypeID) ? 1 : -1);
            this.isLoading = false;
            this.recordsCounter = this.sales.length;

            this.initFilters(this.sales);
            this.getTotal(this.sales);
            this.getSubTotals(this.sales);
        });
    }

    getNETAmount(amount: number): number {
        const net = (amount - (0.101807 * amount));
        return net;
        return (Math.round(net * 100) / 100);
    }

    getTVA(currencyID: number): string {
        switch (currencyID) {
            case Enums.eCurrencyType.RON:
                return 'Da';

            case Enums.eCurrencyType.EUR:
                return 'Nu';

            default:
                return 'Nedefinit';
        }
    }

    updateRowGroupMetaData(sales: Sale[]): void {
        this.rowGroupMetadata = {};

        if (sales) {
            this.parkingName = sales[0].zone;
            for (let i = 0; i < sales.length; i++) {
                const rowData = sales[i];
                const zone = rowData.zone;
                const paymentMethodType = rowData.paymentMethodType;
                if (i === 0) {
                    this.rowGroupMetadata[zone] = { index: 0, size: 1 };
                    this.rowGroupMetadata[zone][paymentMethodType] = { index: 0, size: 1 };
                } else {
                    const previousRowData = sales[i - 1];
                    const previousRowGroupZone = previousRowData.zone;

                    const previousRowGroupPayment = previousRowData.paymentMethodType;
                    if (zone === previousRowGroupZone) {
                        this.rowGroupMetadata[zone].size++;
                        if (paymentMethodType === previousRowGroupPayment) {
                            if (!this.rowGroupMetadata[zone][paymentMethodType]) {
                                this.rowGroupMetadata[zone][paymentMethodType] = { index: i - 1, size: 1 };
                            }
                            this.rowGroupMetadata[zone][paymentMethodType].size++;
                        }
                    } else {
                        this.rowGroupMetadata[zone] = { index: i, size: 1 };
                        this.rowGroupMetadata[zone][paymentMethodType] = { index: i, size: 1 };
                    }
                }
            }
        }
    }

    initFilters(sales: Sale[]): void {
        const defaultLabel: SelectItem = { label: 'Toate', value: null };
        const zoneList = [defaultLabel];
        const paymentMethodList = [defaultLabel];
        const productList = [defaultLabel];

        for (const sale of sales) {
            if (zoneList.findIndex(s => s.value === sale.zone) < 0) {
                zoneList.push({ label: sale.zone, value: sale.zone });
            }

            if (paymentMethodList.findIndex(s => s.value === sale.paymentMethodType) < 0) {
                paymentMethodList.push({ label: sale.paymentMethodType, value: sale.paymentMethodType });
            }

            productList.push({ label: sale.code, value: sale.code });
        }

        this.zoneList = zoneList;
        this.paymentMethodList = paymentMethodList;
        this.productList = productList;
    }

    getTotal(sales: Sale[]): void {
        const smsTotal: Total = {
            paymentMethod: 'SMS',
            qty: 0,
            amount: 0,
            amountNET: 0,
            commission: 0,
            currency: 'EURO'
        };

        const cardTotal: Total = {
            paymentMethod: 'Card',
            qty: 0,
            amount: 0,
            amountNET: 0,
            commission: 0,
            currency: 'RON'
        };

        for (const sale of sales) {

            if (sale.paymentMethodTypeID === Enums.ePaymentMethodType.SMS) {
                smsTotal.amount = smsTotal.amount + sale.amount;
                smsTotal.amountNET = smsTotal.amountNET + sale.amountNET;
                smsTotal.commission = smsTotal.commission + sale.commission;
                smsTotal.qty = smsTotal.qty + sale.qty;
            } else if (sale.paymentMethodTypeID === Enums.ePaymentMethodType.Card) {
                cardTotal.amount = cardTotal.amount + sale.amount;
                cardTotal.amountNET = cardTotal.amountNET + sale.amountNET;
                cardTotal.commission = cardTotal.commission + sale.commission;
                cardTotal.qty = cardTotal.qty + sale.qty;
            }
        }

        this.total = [cardTotal, smsTotal];
    }

    getSubTotals(sales: Sale[]): void {
        const sortedSales = sales.sort((a, b) => (a.zone < b.zone) ? 1 : -1)
        const newSales: Sale[] = [];

        for (let i = 0; i < sortedSales.length; i++) {
            if (i === 0) {
                const sale: Sale = {
                    ...sortedSales[i],
                    code: 'ZTotal',
                    description: ''
                };

                newSales.push(sale);
            } else {
                const previousSale = sortedSales[i - 1];

                if (previousSale.zone === sortedSales[i].zone && previousSale.paymentMethodTypeID === sortedSales[i].paymentMethodTypeID) {
                    const lastSale = newSales[newSales.length - 1];

                    lastSale.amount = lastSale.amount + sortedSales[i].amount;
                    lastSale.amountNET = lastSale.amountNET + sortedSales[i].amountNET;
                    lastSale.commission = lastSale.commission + sortedSales[i].commission;
                    lastSale.qty = lastSale.qty + sortedSales[i].qty;
                } else {
                    const sale: Sale = {
                        ...sortedSales[i],
                        code: 'ZTotal',
                        description: ''
                    };

                    newSales.push(sale);
                }
            }
        }

        this.sales = orderBy([...sortedSales, ...newSales], ['zone', 'paymentMethodType', 'code'], ['desc', 'desc', 'asc']);
        for (const sale of this.sales) {
            if (sale.code === 'ZTotal')
                sale.code = 'Total';
        }
    }

    onSort(): void {
        if (this.filteredSales)
            this.updateRowGroupMetaData(this.filteredSales);
        else
            this.updateRowGroupMetaData(this.sales);
    }

    handleFilter(event): void {
        const sales = event.filteredValue;
        this.filteredSales = [...sales];
        this.recordsCounter = this.filteredSales.length;

        this.updateRowGroupMetaData(sales);
    }
}

interface Total {
    paymentMethod: string;
    qty: number;
    amount: number;
    amountNET: number;
    commission: number;
    currency: string;
}